import { ChangeDetectorRef, Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators
} from '@angular/forms';
import { AHSTripettoComponent } from '@nx-ahs/script-collector';
import { AhsTextComponent } from '@nx-ahs/base-ahs-controls/reactive';
import { Subscription } from 'rxjs';

@Component({
	selector: 'nx-ahs-lockin-date-text',
	templateUrl: './lockin-date-text.component.html',
	styleUrls: ['./lockin-date-text.component.scss'],
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, AhsTextComponent]
})
export class LockinDateTextComponent extends AHSTripettoComponent implements OnDestroy, AfterViewInit, OnInit {
	public customValidationMessages: Map<string, string>;
	public formGroup: FormGroup;
	public placeholder: string = '';
	public label: string;
	public description: string;
	public data;
	public coverageStart: string;
	public coverageEnd: string;

	private subscriptions: Subscription[] = [];

	constructor(
		private formBuilder: FormBuilder,
		private changeDetectorRef: ChangeDetectorRef
	) {
		super();
	}

	ngOnInit(): void {
		this.buildForm();
	}

	private buildForm() {
		this.formGroup = this.formBuilder.group({
			newStartDate: [null, []]
		});
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	async ngAfterViewInit(): Promise<void> {
		const coverageStart = Date.parse(this.coverageStart);
		const coverageEnd = Date.parse(this.coverageEnd);

		this.formGroup.controls['newStartDate'].setValidators([
			Validators.required,
			this.ValidateDate(coverageStart, coverageEnd)
		]);

		this.changeDetectorRef.detectChanges();
	}

	public markAllFieldsAsTouched() {
		this.setTripettoValue(this.formGroup.valid ? this.formGroup.controls['newStartDate'].value : null);

		// Since the Tripetto script only simply checks for "required", i.e. having a value (simple, JSON, whatever),
		// it doesn't care about specific validation of the sub-controls of the inner form... so in the case of
		// a validation error, we'll set "null" to fail the simple "required" idea.
		Object.keys(this.formGroup.controls).forEach((key) => {
			this.formGroup.get(key).markAsTouched();
		});
	}

	private ValidateDate(coverageStart: number, coverageEnd: number): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			// First determine if it's valid at all
			const enteredDate = Date.parse(control.value);
			if (isNaN(enteredDate)) {
				return { validDate: 'Date is not valid' };
			}

			// Next we need to ensure it's between coverage start and end dates
			if (enteredDate < coverageStart || enteredDate > coverageEnd) {
				const startDate = new Date(coverageStart).toLocaleDateString('en-us', {
					year: 'numeric',
					month: 'short',
					day: 'numeric'
				});
				const endDate = new Date(coverageEnd).toLocaleDateString('en-us', {
					year: 'numeric',
					month: 'short',
					day: 'numeric'
				});

				return { validDate: `Date must be between ${startDate} and ${endDate}` };
			}
		};
	}
}
